import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';

interface Block {
  x: number;
  y: number;
}

type BlockType = Block[];

const COLORS = ['#00f0f0', '#0000f0', '#f0a000', '#f0f000', '#00f000', '#f00000', '#a000f0'];
const BOARD_WIDTH = 10;
const BOARD_HEIGHT = 20;
const BLOCK_SIZE = 30;
const PREVIEW_SIZE = 4;
const INITIAL_DROP_TIME = 1000;

const GameContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 20px;
  padding: 20px;
`;

const GameBoard = styled.div`
  position: relative;
  width: ${BOARD_WIDTH * BLOCK_SIZE}px;
  height: ${BOARD_HEIGHT * BLOCK_SIZE}px;
  background-color: white;
  border: 2px solid #333;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
`;

const PreviewBox = styled.div`
  width: ${PREVIEW_SIZE * BLOCK_SIZE}px;
  height: ${PREVIEW_SIZE * BLOCK_SIZE}px;
  background-color: white;
  border: 2px solid #333;
  position: relative;
  margin-bottom: 15px;
`;

const PreviewPiece = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
`;

interface StyledBlockProps {
  left: number;
  top: number;
  bg?: string;
}

const Block = styled.div<StyledBlockProps>`
  position: absolute;
  width: ${BLOCK_SIZE - 2}px;
  height: ${BLOCK_SIZE - 2}px;
  left: ${(props: StyledBlockProps) => props.left * BLOCK_SIZE}px;
  top: ${(props: StyledBlockProps) => props.top * BLOCK_SIZE}px;
  background: ${(props: StyledBlockProps) => props.bg || 'transparent'};
  border: ${props => props.bg ? '1px solid rgba(0,0,0,0.2)' : 'none'};
  box-sizing: border-box;
  box-shadow: ${props => props.bg ? 'inset 0 0 10px rgba(255,255,255,0.5)' : 'none'};
`;

const InfoPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 150px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
`;

const InfoItem = styled.div`
  padding: 10px 15px;
  background: #f8f8f8;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  color: #333;
  text-align: center;
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  background: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #357abd;
  }

  &:active {
    background: #2a5f9e;
  }
`;

// Standard Tetris shapes - fixed positions
const TETROMINOS = {
  I: [[1,0], [1,1], [1,2], [1,3]],     // I piece (horizontal)
  O: [[0,0], [0,1], [1,0], [1,1]],     // O piece (2x2 square)
  T: [[1,1], [0,1], [1,0], [1,2]],     // T piece
  L: [[1,0], [1,1], [1,2], [0,2]],     // L piece
  J: [[1,0], [1,1], [1,2], [0,0]],     // J piece
  S: [[1,0], [1,1], [0,1], [0,2]],     // S piece
  Z: [[0,0], [0,1], [1,1], [1,2]]      // Z piece
};

const GameOverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
`;

const GameOverDialog = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  width: 80%;
  max-width: 300px;
  text-align: center;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
`;

const GameOverTitle = styled.h2`
  color: #e74c3c;
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 24px;
`;

const GameOverScore = styled.p`
  font-size: 18px;
  margin-bottom: 20px;
`;

const GameOverButtons = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
`;

const GameOverButton = styled.button`
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
`;

const RestartButton = styled(GameOverButton)`
  background: #4a90e2;
  color: white;
  
  &:hover {
    background: #357abd;
  }
`;

const CloseButton = styled(GameOverButton)`
  background: #e74c3c;
  color: white;
  
  &:hover {
    background: #c0392b;
  }
`;

export const TetrisGame: React.FC = () => {
  const [board, setBoard] = useState<(string | null)[][]>(
    Array(BOARD_HEIGHT).fill(null).map(() => Array(BOARD_WIDTH).fill(null))
  );
  const [currentPiece, setCurrentPiece] = useState<BlockType | null>(null);
  const [nextPiece, setNextPiece] = useState<BlockType | null>(null);
  const [nextPieceType, setNextPieceType] = useState<keyof typeof TETROMINOS | null>(null);
  const [currentColor, setCurrentColor] = useState<string>('');
  const [nextColor, setNextColor] = useState<string>('');
  const [score, setScore] = useState(0);
  const [lines, setLines] = useState(0);
  const [level, setLevel] = useState(0);
  const [gameStatus, setGameStatus] = useState<'stopped' | 'playing' | 'paused' | 'gameover'>('stopped');
  const [showGameOver, setShowGameOver] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const moveDownRef = useRef<() => void>();
  const movementTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const centerPieceInPreview = (piece: number[][]) => {
    // Get the dimensions of the piece
    const minX = Math.min(...piece.map(([_, x]) => x));
    const maxX = Math.max(...piece.map(([_, x]) => x));
    const minY = Math.min(...piece.map(([y, _]) => y));
    const maxY = Math.max(...piece.map(([y, _]) => y));
    
    const pieceWidth = maxX - minX + 1;
    const pieceHeight = maxY - minY + 1;
    
    // Calculate offsets to center the piece
    const offsetX = Math.floor((PREVIEW_SIZE - pieceWidth) / 2);
    const offsetY = Math.floor((PREVIEW_SIZE - pieceHeight) / 2);
    
    // Apply offsets and adjust for the piece's current position
    return piece.map(([y, x]) => ({
      x: x - minX + offsetX,
      y: y - minY + offsetY
    }));
  };

  const generatePiece = () => {
    const pieces = Object.keys(TETROMINOS) as (keyof typeof TETROMINOS)[];
    const pieceType = pieces[Math.floor(Math.random() * pieces.length)];
    const piece = TETROMINOS[pieceType];
    const color = COLORS[Math.floor(Math.random() * COLORS.length)];
    return { piece, color, pieceType };
  };

  // Initialize only next piece on mount
  useEffect(() => {
    const next = generatePiece();
    setNextPiece(centerPieceInPreview(next.piece));
    setNextPieceType(next.pieceType);
    setNextColor(next.color);
  }, []);

  const isValidMove = (piece: BlockType, board: (string | null)[][]) => {
    return piece.every(block => {
      return block.x >= 0 && 
             block.x < BOARD_WIDTH &&
             block.y < BOARD_HEIGHT &&
             (block.y < 0 || board[block.y][block.x] === null);
    });
  };

  const rotate = () => {
    if (!currentPiece || gameStatus !== 'playing') return;
    
    // Find the center block (usually at index 1 except for O piece)
    const centerBlock = currentPiece[1];
    
    // O piece doesn't rotate
    if (currentPiece.every((block, i) => 
      i === 0 ? block.x === centerBlock.x && block.y === centerBlock.y - 1 :
      i === 1 ? block.x === centerBlock.x && block.y === centerBlock.y :
      i === 2 ? block.x === centerBlock.x + 1 && block.y === centerBlock.y :
      block.x === centerBlock.x + 1 && block.y === centerBlock.y - 1
    )) {
      return; // This is O piece, don't rotate
    }
    
    // Rotate around the center block
    const rotatedPiece = currentPiece.map(block => ({
      x: centerBlock.x - (block.y - centerBlock.y),
      y: centerBlock.y + (block.x - centerBlock.x)
    }));

    // Try rotation with wall kicks
    if (isValidMove(rotatedPiece, board)) {
      setCurrentPiece(rotatedPiece);
    } else {
      // Try moving left
      const kickLeft = rotatedPiece.map(block => ({ ...block, x: block.x - 1 }));
      if (isValidMove(kickLeft, board)) {
        setCurrentPiece(kickLeft);
        return;
      }
      // Try moving right
      const kickRight = rotatedPiece.map(block => ({ ...block, x: block.x + 1 }));
      if (isValidMove(kickRight, board)) {
        setCurrentPiece(kickRight);
      }
    }
  };

  const moveDown = () => {
    if (!currentPiece || gameStatus !== 'playing') return;
    
    const newPiece = currentPiece.map(block => ({
      ...block,
      y: block.y + 1
    }));

    if (isValidMove(newPiece, board)) {
      setCurrentPiece(newPiece);
    } else {
      // Lock the piece with its color
      const newBoard = [...board];
      currentPiece.forEach(block => {
        if (block.y >= 0) {
          newBoard[block.y][block.x] = currentColor;
        }
      });
      
      // Check for completed lines from bottom to top
      let linesCleared = 0;
      for (let y = BOARD_HEIGHT - 1; y >= 0; y--) {
        if (newBoard[y].every(cell => cell !== null)) {
          linesCleared++;
          newBoard.splice(y, 1);
          newBoard.unshift(Array(BOARD_WIDTH).fill(null));
          y++; // Check the same row again
        }
      }
      
      if (linesCleared > 0) {
        setLines(prev => prev + linesCleared);
        setScore(prev => prev + linesCleared * 100 * (level + 1));
      }

      // Generate new current piece and next piece
      const next = generatePiece();
      
      // Create new piece using the original tetromino definition, not the preview version
      let newCurrentPiece: BlockType | null = null;
      if (nextPieceType) {
        const originalShape = TETROMINOS[nextPieceType];
        newCurrentPiece = originalShape.map(([y, x]) => ({
          x: x + Math.floor(BOARD_WIDTH / 2) - 1,
          y: y
        }));
      }

      // Check for game over
      if (newCurrentPiece && !isValidMove(newCurrentPiece, newBoard)) {
        setGameStatus('gameover');
        setShowGameOver(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      } else {
        // Continue game with new piece
        setCurrentPiece(newCurrentPiece);
        setCurrentColor(nextColor);
        setNextPiece(centerPieceInPreview(next.piece));
        setNextPieceType(next.pieceType);
        setNextColor(next.color);
      }
      
      setBoard(newBoard);
    }
  };

  // Store moveDown in ref to avoid stale closure in interval
  moveDownRef.current = moveDown;

  const moveLeft = () => {
    if (!currentPiece || gameStatus !== 'playing') return;
    
    // Clear any existing movement timeout
    if (movementTimeoutRef.current) {
      clearTimeout(movementTimeoutRef.current);
    }
    
    const newPiece = currentPiece.map(block => ({
      ...block,
      x: block.x - 1
    }));
    
    if (isValidMove(newPiece, board)) {
      setCurrentPiece(newPiece);
    }
    
    // Set a small timeout before allowing next movement
    movementTimeoutRef.current = setTimeout(() => {
      movementTimeoutRef.current = undefined;
    }, 50);
  };

  const moveRight = () => {
    if (!currentPiece || gameStatus !== 'playing') return;
    
    // Clear any existing movement timeout
    if (movementTimeoutRef.current) {
      clearTimeout(movementTimeoutRef.current);
    }
    
    const newPiece = currentPiece.map(block => ({
      ...block,
      x: block.x + 1
    }));
    
    if (isValidMove(newPiece, board)) {
      setCurrentPiece(newPiece);
    }
    
    // Set a small timeout before allowing next movement
    movementTimeoutRef.current = setTimeout(() => {
      movementTimeoutRef.current = undefined;
    }, 50);
  };

  const hardDrop = () => {
    if (!currentPiece || gameStatus !== 'playing') return;
    
    let newY = 0;
    let canFall = true;
    
    // Find the lowest valid position
    while (canFall) {
      const testPiece = currentPiece.map(block => ({
        ...block,
        y: block.y + newY + 1
      }));
      
      if (isValidMove(testPiece, board)) {
        newY++;
      } else {
        canFall = false;
      }
    }
    
    // Move piece to final position
    const finalPiece = currentPiece.map(block => ({
      ...block,
      y: block.y + newY
    }));
    
    setCurrentPiece(finalPiece);
    moveDown(); // This will lock the piece and generate next piece
  };

  const startGame = () => {
    // Clear the board
    setBoard(Array(BOARD_HEIGHT).fill(null).map(() => Array(BOARD_WIDTH).fill(null)));
    setScore(0);
    setLines(0);
    setLevel(0);
    setShowGameOver(false);
    
    // Use next piece as first piece and generate new next piece
    const next = generatePiece();
    setCurrentPiece(nextPiece?.map(block => ({
      x: block.x + Math.floor(BOARD_WIDTH / 2) - 1,
      y: block.y
    })) || null);
    setCurrentColor(nextColor);
    setNextPiece(centerPieceInPreview(next.piece));
    setNextPieceType(next.pieceType);
    setNextColor(next.color);
    setGameStatus('playing');
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      moveDownRef.current?.();
    }, INITIAL_DROP_TIME - level * 100);
  };

  const togglePause = () => {
    if (gameStatus === 'playing') {
      setGameStatus('paused');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    } else if (gameStatus === 'paused') {
      setGameStatus('playing');
      intervalRef.current = setInterval(() => {
        moveDownRef.current?.();
      }, INITIAL_DROP_TIME - level * 100);
    }
  };

  const closeGameOver = () => {
    setShowGameOver(false);
    setGameStatus('stopped');
  };

  // Clean up all timeouts and intervals on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (movementTimeoutRef.current) {
        clearTimeout(movementTimeoutRef.current);
      }
    };
  }, []);

  // Update keyboard handler to use hardDrop
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (gameStatus !== 'playing') return;
      
      switch (e.key) {
        case 'ArrowLeft':
          moveLeft();
          break;
        case 'ArrowRight':
          moveRight();
          break;
        case 'ArrowDown':
          moveDown();
          break;
        case 'ArrowUp':
          rotate();
          break;
        case ' ':
          e.preventDefault(); // Prevent page scroll
          hardDrop();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPiece, board, gameStatus]);

  return (
    <GameContainer>
      <GameBoard>
        {board.map((row, y) =>
          row.map((cell, x) => (
            <Block key={`${y}-${x}`} left={x} top={y} bg={cell || undefined} />
          ))
        )}
        {currentPiece?.map((block, index) => (
          <Block
            key={`piece-${index}`}
            left={block.x}
            top={block.y}
            bg={currentColor}
          />
        ))}
        {showGameOver && (
          <GameOverOverlay>
            <GameOverDialog>
              <GameOverTitle>Game Over</GameOverTitle>
              <GameOverScore>Your score: {score}</GameOverScore>
              <GameOverButtons>
                <RestartButton onClick={startGame}>Restart</RestartButton>
                <CloseButton onClick={closeGameOver}>Close</CloseButton>
              </GameOverButtons>
            </GameOverDialog>
          </GameOverOverlay>
        )}
      </GameBoard>
      <InfoPanel>
        <PreviewBox>
          <PreviewPiece>
            {nextPiece?.map((block, index) => (
              <Block
                key={`preview-${index}`}
                left={block.x}
                top={block.y}
                bg={nextColor}
              />
            ))}
          </PreviewPiece>
        </PreviewBox>
        <InfoItem>SCORE: {score}</InfoItem>
        <InfoItem>LINES: {lines}</InfoItem>
        <InfoItem>LEVEL: {level}</InfoItem>
        <Button onClick={gameStatus === 'stopped' ? startGame : togglePause}>
          {gameStatus === 'stopped' ? 'Start' : gameStatus === 'paused' ? 'Continue' : 'Pause'}
        </Button>
        <Button onClick={startGame} style={{ background: '#e74c3c' }}>
          Restart
        </Button>
      </InfoPanel>
    </GameContainer>
  );
};

export default TetrisGame; 